// src/hooks/useWeeklyReviews.js
// Side-effect hook that auto-generates weekly budget review tasks for all
// projects in rehab status. Runs once per session on mount, gated by the
// enableWeeklyReviews feature toggle. Deduplicates via ISO week IDs stored
// on each task (_weekId field) and a session-level useRef guard.

import { useEffect, useRef } from 'react';
import { resolveProjectPhase, resolveProjectStatus } from '../utils/phaseMapping';

// ── Helpers ──────────────────────────────────────────────────────────────

/** Returns ISO week string like "2025-W07" for deduplication */
function getISOWeekId(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/** Returns next Friday (or today if already Friday) as YYYY-MM-DD */
function getNextFriday(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 5=Fri
  const daysUntilFriday = day <= 5 ? 5 - day : 5 + (7 - day);
  d.setDate(d.getDate() + (daysUntilFriday === 0 ? 0 : daysUntilFriday));
  return d.toISOString().split('T')[0];
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function getWeeklyReviewProjects(projects = []) {
  return projects.filter((p) => {
    const phase = resolveProjectPhase(p);
    const status = resolveProjectStatus(p);
    const isCompleted = status === 'rehab_complete' || (p.progress || 0) >= 100;
    return phase === 'rehab' && !isCompleted;
  });
}

export function useWeeklyReviews({
  projects,
  updateProjectInFirebase,
  addNotification,
  isFeatureEnabled,
}) {
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Guard: only run once per session
    if (hasRunRef.current) return;

    // Guard: feature must be enabled
    if (!isFeatureEnabled || !isFeatureEnabled('enableWeeklyReviews')) return;

    // Guard: wait for projects to load
    if (!projects || projects.length === 0) return;

    // Guard: need Firebase helpers
    if (!updateProjectInFirebase || !addNotification) return;

    // Mark as run immediately to prevent double-fire
    hasRunRef.current = true;

    const currentWeekId = getISOWeekId();
    const dueDate = getNextFriday();
    let tasksCreated = 0;

    // Filter to rehab projects that haven't completed
    const rehabProjects = getWeeklyReviewProjects(projects);

    for (const project of rehabProjects) {
      const existingTasks = project.tasks || [];

      // Check if a review task already exists for this week
      const alreadyExists = existingTasks.some(
        (t) => t._weeklyReview && t._weekId === currentWeekId
      );

      if (alreadyExists) continue;

      // Create new weekly review task
      const newTask = {
        id: Date.now() + Math.random(), // Ensure unique across batch
        name: `📋 Weekly Budget Review — ${project.address || 'Unknown'}`,
        completed: false,
        dueDate,
        _weeklyReview: true,
        _weekId: currentWeekId,
      };

      const updatedTasks = [...existingTasks, newTask];

      // Persist to Firebase
      updateProjectInFirebase(project.id, {
        tasks: updatedTasks,
        _lastWeeklyReviewWeek: currentWeekId,
      });

      tasksCreated++;
    }

    // Send summary notification if tasks were created
    if (tasksCreated > 0) {
      addNotification({
        title: 'Weekly Reviews Generated',
        message: `${tasksCreated} weekly budget review task${tasksCreated > 1 ? 's' : ''} created for rehab projects (due ${dueDate}).`,
        type: 'review',
        teamBroadcast: true,
      });
    }
  }, [projects, updateProjectInFirebase, addNotification, isFeatureEnabled]);
}
